/**
 * The /surprise/:token page — a shared "magic box" link.
 *
 * Flow (login-before-open, because the prize is drawn + reserved per
 * account):
 *   load view → box floats → [sign in if needed] → open (draw+reveal)
 *             → Claim → receipt
 *
 * The reveal shows the REAL drawn prize (the backend reserves it with a
 * TTL), so Claim simply commits the held code. Re-opening an unclaimed
 * hold shows the same prize; an already-claimed visitor sees their gift.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../auth/hooks/useAuth';
import { CouponApiError } from '../../redeem/services/couponApi';
import { getSurpriseView, openSurprise, claimSurprise } from '../services/surpriseApi';
import type {
  SurpriseView, SurpriseRewardPreview, SurpriseStep,
} from '../types';
import type { Reward } from '../../redeem/types';
import MagicBox, { type MagicTheme } from './MagicBox';

/** Brand → public logo path for the lid wax-seal. */
function sealFor(brand: string | null | undefined): string {
  const b = brand || 'autopilot';
  return `/brands/${b}/logo.webp`;
}

/** Light, brand-accent theme for the magic box (mirrors config/brand.ts). */
const THEMES: Record<string, MagicTheme> = {
  autopilot: {
    accent: '#c45a20', accent2: '#d4702e', accentSoft: '#e8935a', accentDeep: '#9a4010',
    accentRgb: '196,90,32', accent2Rgb: '212,112,46', btnText: '#ffffff',
  },
  onbuzz: {
    accent: '#eab308', accent2: '#facc15', accentSoft: '#fde68a', accentDeep: '#ca8a04',
    accentRgb: '234,179,8', accent2Rgb: '250,204,21', btnText: '#3a2a12',
  },
};
function themeFor(brand: string | null | undefined): MagicTheme {
  return THEMES[brand || 'autopilot'] || THEMES.autopilot;
}

/** Does this reward only pay out once the user subscribes? */
function isSubscribeGated(r: Reward | undefined): boolean {
  if (!r) return false;
  if (r.type === 'credits_on_subscribe' || r.type === 'months_on_subscribe') return true;
  if (r.type === 'combo') return r.rewards.some(isSubscribeGated);
  return false;
}

/** Where to send the user after they claim — subscribe-gated rewards point
 *  at /pricing to unlock; everything already-applied points at /dashboard. */
function claimedCta(reward: Reward | undefined): { to: string; label: string } {
  return isSubscribeGated(reward)
    ? { to: '/pricing', label: 'See plans to unlock it' }
    : { to: '/dashboard', label: 'Go to your dashboard' };
}

/** Dark full-screen wrapper for the non-box message states. */
const MessagePanel: React.FC<{ title: string; body?: React.ReactNode; spinner?: boolean }> = ({
  title, body, spinner,
}) => (
  <div className="mb-root" data-testid="surprise-panel">
    <div className="mb-panel">
      {spinner && <div className="mb-spinner" aria-hidden="true" />}
      <div className="mb-panel-title">{title}</div>
      {body && <div className="mb-panel-body">{body}</div>}
    </div>
  </div>
);

const SurpriseLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<SurpriseStep>('loading');
  const [view, setView] = useState<SurpriseView | null>(null);
  const [reveal, setReveal] = useState<SurpriseRewardPreview | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mapError = useCallback((err: CouponApiError) => {
    switch (err.code) {
      case 'NOT_FOUND': setStep('not_found'); break;
      case 'EXPIRED': setStep('expired'); break;
      case 'BATCH_EXHAUSTED' as CouponApiError['code']: setStep('exhausted'); break;
      default:
        setStep('error');
        setErrorMessage(err.message || 'Something went wrong. Please try again.');
    }
  }, []);

  // ---- load the view on mount / token change ----
  useEffect(() => {
    if (!token) { setStep('not_found'); return; }
    let cancelled = false;
    setStep('loading');
    getSurpriseView(token)
      .then((v) => {
        if (cancelled) return;
        setView(v);
        if (v.alreadyClaimed) { setReveal(v.alreadyClaimed); setStep('claimed'); }
        else if (v.reserved) { setReveal(v.reserved); setStep('revealed'); }
        else if (v.exhausted) { setStep('exhausted'); }
        else { setStep('idle'); }
      })
      .catch((err) => { if (!cancelled) mapError(err as CouponApiError); });
    return () => { cancelled = true; };
  }, [token, mapError]);

  // ---- open: draw + reserve a prize (requires auth) ----
  const handleOpen = useCallback(async () => {
    if (!token) return;
    if (!isAuthenticated) {
      // Bounce to login and return to this exact link afterwards.
      navigate('/login', { state: { from: { pathname: `/surprise/${token}` } } });
      return;
    }
    setStep('opening');
    setErrorMessage(null);
    try {
      const r = await openSurprise(token);
      setReveal(r);
      setStep(r.alreadyClaimed ? 'claimed' : 'revealed');
    } catch (err) {
      mapError(err as CouponApiError);
    }
  }, [token, isAuthenticated, navigate, mapError]);

  // ---- claim: commit the held prize ----
  const handleClaim = useCallback(async () => {
    if (!token) return;
    setStep('claiming');
    try {
      await claimSurprise(token);
      setStep('claimed');
    } catch (err) {
      const apiErr = err as CouponApiError;
      if (apiErr.code === ('NO_RESERVATION' as CouponApiError['code'])) {
        // Hold lapsed — send them back to re-open for a fresh draw.
        setReveal(null);
        setStep('idle');
        setErrorMessage('Your gift slipped away — open the box again.');
        return;
      }
      mapError(apiErr);
    }
  }, [token, mapError]);

  // ---- message states ----
  if (step === 'loading') {
    return <MessagePanel title="A gift is on its way…" spinner />;
  }
  if (step === 'not_found') {
    return <MessagePanel title="This link isn’t active" body="The surprise link is invalid or has been turned off." />;
  }
  if (step === 'expired') {
    return <MessagePanel title="This surprise has ended" body="The link has expired. Reach out to whoever shared it with you." />;
  }
  if (step === 'exhausted') {
    return <MessagePanel title="Every gift has found a home 🎁" body="All the surprises from this link have been claimed. Thanks for stopping by!" />;
  }
  if (step === 'error') {
    return <MessagePanel title="Something went wrong" body={errorMessage || 'Please try again in a moment.'} />;
  }

  // ---- box states (idle / opening / revealed / claiming / claimed) ----
  const firstName = (user && (user as { firstName?: string }).firstName) || '';
  const boxOpen = step === 'revealed' || step === 'claiming' || step === 'claimed';

  const greeting = view?.greeting || 'A gift is waiting for you';
  const greetingLine = firstName ? `${firstName}, ${greeting.charAt(0).toLowerCase()}${greeting.slice(1)}` : greeting;

  const hint = (
    <div data-testid="surprise-tag">
      <div className="mb-tag-greeting">{greetingLine}</div>
      {view?.sender && <div className="mb-tag-sender">— {view.sender}</div>}
      <button
        className="mb-open-btn"
        onClick={handleOpen}
        disabled={step === 'opening'}
        data-testid="surprise-open"
      >
        {step === 'opening'
          ? 'Opening…'
          : isAuthenticated ? 'Open your gift' : 'Sign in to open your gift'}
      </button>
      {errorMessage && <div className="mb-remaining">{errorMessage}</div>}
      {typeof view?.giftsRemaining === 'number' && view.giftsRemaining > 0 && (
        <div className="mb-remaining">{view.giftsRemaining.toLocaleString()} gifts remaining</div>
      )}
    </div>
  );

  const prize = reveal ? (
    <div className="mb-card" data-testid="surprise-prize">
      <div className="mb-eyebrow">{step === 'claimed' ? 'Your gift' : 'You unwrapped'}</div>
      <div className="mb-title">{reveal.tierName}</div>
      <div className="mb-rule" />
      <div className="mb-detail">{reveal.summary}</div>
      {step === 'claimed' ? (
        <>
          <div className="mb-claim-note" data-testid="surprise-claimed">
            ✓ Added to your account{firstName ? `, ${firstName}` : ''}. Enjoy!
          </div>
          <button
            className="mb-claim-btn"
            onClick={() => navigate(claimedCta(reveal?.reward).to)}
            data-testid="surprise-done"
          >
            {claimedCta(reveal?.reward).label}
          </button>
        </>
      ) : (
        <>
          <button
            className="mb-claim-btn"
            onClick={handleClaim}
            disabled={step === 'claiming'}
            data-testid="surprise-claim"
          >
            {step === 'claiming' ? 'Claiming…' : 'Claim it'}
          </button>
          <div className="mb-claim-note">Applied straight to your account.</div>
        </>
      )}
    </div>
  ) : null;

  return (
    <MagicBox
      open={boxOpen}
      theme={themeFor(view?.brand)}
      sealSrc={sealFor(view?.brand)}
      prize={prize}
      hint={hint}
      onBoxClick={isAuthenticated ? handleOpen : undefined}
    />
  );
};

export default SurpriseLanding;
